import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  MapPin, Plus, Search, Trash2, Upload, RefreshCw, Loader2, CheckCircle2,
  Download, Sparkles,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CSV_TEMPLATE = 'pin,area_name,enabled\n562159,Ramanagara Town,true\n562120,Channapatna,true\n';

export default function ServiceArea() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ pin: '', area_name: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [togglingPin, setTogglingPin] = useState(null);
  const [deletingPin, setDeletingPin] = useState(null);
  const csvRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + '/service-pincodes', { headers: authHeaders() });
      setRows(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load service PINs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? rows.filter(r => {
        const t = search.toLowerCase();
        return (r.pin || '').includes(t) || (r.area_name || '').toLowerCase().includes(t);
      })
    : rows;

  const enabledCount = rows.filter(r => r.enabled).length;

  const onAdd = async () => {
    const pin = (draft.pin || '').trim();
    const area_name = (draft.area_name || '').trim();
    if (!/^\d{6}$/.test(pin)) { toast.error('PIN must be exactly 6 digits'); return; }
    if (!area_name) { toast.error('Area name is required'); return; }
    setSaving(true);
    try {
      await axios.post(API + '/service-pincodes', { pin, area_name, enabled: draft.enabled },
        { headers: authHeaders() });
      toast.success(`PIN ${pin} added & synced to OG App`);
      setAddOpen(false);
      setDraft({ pin: '', area_name: '', enabled: true });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add PIN');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (row) => {
    setTogglingPin(row.pin);
    try {
      await axios.put(API + '/service-pincodes/' + row.pin,
        { enabled: !row.enabled }, { headers: authHeaders() });
      toast.success(`PIN ${row.pin} ${!row.enabled ? 'enabled' : 'disabled'} — OG synced`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle');
    } finally {
      setTogglingPin(null);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Remove PIN ${row.pin} (${row.area_name})? This is permanent.`)) return;
    setDeletingPin(row.pin);
    try {
      await axios.delete(API + '/service-pincodes/' + row.pin, { headers: authHeaders() });
      toast.success(`PIN ${row.pin} removed — OG synced`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingPin(null);
    }
  };

  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(API + '/service-pincodes/sync-og', {},
        { headers: authHeaders() });
      toast.success(`Pushed ${res.data.pincode_count} enabled PINs to OG App`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const onSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(API + '/service-pincodes/seed-default', {},
        { headers: authHeaders() });
      const inserted = res.data?.inserted ?? 0;
      toast.success(inserted
        ? `Seeded ${inserted} starter Ramanagara PINs`
        : 'All starter PINs already present');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(API + '/service-pincodes/bulk-csv', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const { inserted = 0, updated = 0, skipped_count = 0 } = res.data || {};
      toast.success(`CSV imported — ${inserted} added, ${updated} updated${skipped_count ? `, ${skipped_count} skipped` : ''} · OG synced`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV import failed');
    } finally {
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'service-pincodes-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="service-area-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <MapPin className="w-7 h-7 text-indigo-600" />
            Service Area (PIN Codes)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Only retailers in <span className="font-semibold text-slate-700">enabled</span> PIN codes can sign up
            and place orders on the OG Retailer App. Every change here auto-syncs to the OG App.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadTemplate}
            data-testid="download-pin-template-btn">
            <Download className="w-4 h-4 mr-1" />CSV Template
          </Button>
          <input ref={csvRef} type="file" accept=".csv" hidden onChange={onCsvUpload}
            data-testid="pin-csv-input" />
          <Button variant="outline" size="sm" onClick={() => csvRef.current?.click()}
            data-testid="pin-bulk-csv-btn">
            <Upload className="w-4 h-4 mr-1" />Bulk Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={onSyncNow} disabled={syncing}
            data-testid="pin-sync-og-btn">
            {syncing
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync to OG App
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="pin-add-btn">
            <Plus className="w-4 h-4 mr-1" />Add PIN
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="pin-stat-total">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total PINs</p>
            <p className="text-2xl font-bold mt-1">{rows.length}</p>
          </CardContent>
        </Card>
        <Card data-testid="pin-stat-enabled">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase tracking-wide font-semibold">Enabled (live on OG)</p>
            <p className="text-2xl font-bold mt-1 text-emerald-700">{enabledCount}</p>
          </CardContent>
        </Card>
        <Card data-testid="pin-stat-disabled">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Disabled</p>
            <p className="text-2xl font-bold mt-1 text-slate-500">{rows.length - enabledCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by PIN or area name..."
            className="pl-8" data-testid="pin-search-input" />
        </div>
        {rows.length === 0 && (
          <Button variant="outline" size="sm" onClick={onSeed} disabled={seeding}
            data-testid="pin-seed-btn">
            {seeding
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <Sparkles className="w-4 h-4 mr-1" />}
            Seed 10 Ramanagara PINs
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Service PINs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="pin-empty-state">
              {rows.length === 0
                ? 'No service PINs yet. Click "Seed 10 Ramanagara PINs" above to start, or add manually.'
                : 'No matches for your search.'}
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-700 text-white">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">PIN</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Area Name</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Enabled</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Last Updated</th>
                    <th className="px-3 py-2.5 text-right text-xs uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => (
                    <tr key={row.pin} className="border-t hover:bg-slate-50"
                      data-testid={`pin-row-${row.pin}`}>
                      <td className="px-3 py-2.5 font-mono font-semibold text-indigo-700">{row.pin}</td>
                      <td className="px-3 py-2.5">{row.area_name}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Switch checked={!!row.enabled}
                            disabled={togglingPin === row.pin}
                            onCheckedChange={() => onToggle(row)}
                            data-testid={`pin-toggle-${row.pin}`} />
                          {row.enabled
                            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Live on OG</Badge>
                            : <Badge variant="outline" className="text-slate-500">Hidden</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">
                        {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button size="sm" variant="ghost"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          onClick={() => onDelete(row)}
                          disabled={deletingPin === row.pin}
                          data-testid={`pin-delete-${row.pin}`}>
                          {deletingPin === row.pin
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pin-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />Add Service PIN
            </DialogTitle>
            <DialogDescription>
              Adds a 6-digit Indian PIN to the service-area list. Every save auto-syncs to the OG Retailer App so the
              new area becomes available for sign-ups immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="pin-add-pin">PIN Code</Label>
              <Input id="pin-add-pin" value={draft.pin}
                onChange={(e) => setDraft(d => ({ ...d, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="e.g. 562159" maxLength={6}
                className="mt-1 font-mono"
                data-testid="pin-add-pin-input" />
            </div>
            <div>
              <Label htmlFor="pin-add-area">Area Name</Label>
              <Input id="pin-add-area" value={draft.area_name}
                onChange={(e) => setDraft(d => ({ ...d, area_name: e.target.value }))}
                placeholder="e.g. Ramanagara Town" className="mt-1"
                data-testid="pin-add-area-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={draft.enabled}
                onCheckedChange={(v) => setDraft(d => ({ ...d, enabled: v }))}
                data-testid="pin-add-enabled-switch" />
              <Label className="cursor-pointer">Visible to OG App immediately</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}
              data-testid="pin-add-cancel">Cancel</Button>
            <Button onClick={onAdd} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="pin-add-save">
              {saving
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Save & Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
